import { useState, useEffect } from 'react';
import { BookService, BookDetail } from '../services/bookService';

/**
 * Custom hook for fetching book details
 * Handles loading states and error handling
 */
export const useBookDetail = (bookId: string | undefined, languageCode: string = 'en') => {
  const [bookDetail, setBookDetail] = useState<BookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookId) {
      setLoading(false);
      setError('No book ID provided');
      return;
    }

    const fetchBookDetail = async () => {
      setLoading(true);
      setError(null);

      const response = await BookService.getBookDetail(bookId, languageCode);

      if (response.success && response.data) {
        setBookDetail(response.data);
      } else {
        setError(response.error || 'Failed to load book');
      }

      setLoading(false);
    };

    fetchBookDetail();
  }, [bookId, languageCode]);

  const refresh = async () => {
    if (!bookId) return;

    // Invalidate cache and refetch
    await BookService.invalidateBookCache(bookId, languageCode);
    
    setLoading(true);
    const response = await BookService.getBookDetail(bookId, languageCode);
    
    if (response.success && response.data) {
      setBookDetail(response.data);
      setError(null);
    } else {
      setError(response.error || 'Failed to refresh book');
    }
    
    setLoading(false);
  };

  return {
    book: bookDetail?.book || null,
    insights: bookDetail?.insights || [],
    loading,
    error,
    refresh
  };
}; 